import React, { useState } from "react";
import Icon from "./Icon";
import "./certification.css";

// Default Pinata gateway (final fallback if no full URL provided)
const DEFAULT_IPFS_GATEWAY = "https://gateway.pinata.cloud/ipfs/";

/**
 * Helper to resolve URL with priority:
 * 1. Direct URL (pdfUrl/jsonUrl)
 * 2. Fallback URLs (finalPdfUrl, ipfs.pdfUrl, ipfsGatewayUrls.pdf)
 * 3. Custom gateway + CID (gatewayExample + pdfCid/jsonCid)
 * 4. Default gateway + CID
 */
const resolveUrl = (type, data) => {
  // type: 'pdf' or 'json'
  const cidKey = type === 'pdf' ? 'pdfCid' : 'jsonCid';
  const urlKey = type === 'pdf' ? 'pdfUrl' : 'jsonUrl';
  const finalUrlKey = type === 'pdf' ? 'finalPdfUrl' : 'finalJsonUrl';
  const ipfsUrlKey = type === 'pdf' ? 'pdfUrl' : 'jsonUrl';
  const gatewayUrlKey = type === 'pdf' ? 'pdf' : 'json';
  
  // Priority 1: Direct URL
  if (data[urlKey]) return data[urlKey];
  
  // Priority 2: Fallback URLs
  if (data[finalUrlKey]) return data[finalUrlKey];
  if (data.ipfs?.[ipfsUrlKey]) return data.ipfs[ipfsUrlKey];
  if (data.ipfsGatewayUrls?.[gatewayUrlKey]) return data.ipfsGatewayUrls[gatewayUrlKey];
  
  // Priority 3: Custom gateway + CID
  const cid = data.ipfs?.[cidKey] || data[cidKey];
  if (cid && data.gatewayExamples?.pinata) {
    return `${data.gatewayExamples.pinata}${cid}`;
  }
  
  // Priority 4: Default gateway + CID (final fallback)
  if (cid) return `${DEFAULT_IPFS_GATEWAY}${cid}`;
  
  return null;
};

/**
 * IPFSLinks component
 * Resolves URLs with priority: direct URLs > fallback URLs > custom gateway > default gateway
 */
const IPFSLinks = ({ 
  jsonCid, 
  pdfCid, 
  jsonUrl,      
  pdfUrl,       
  finalPdfUrl,
  finalJsonUrl,
  ipfs,         // Object with { pdfUrl, jsonUrl, pdfCid, jsonCid }
  ipfsGatewayUrls,  // Object with { pdf, json }
  gatewayExamples,  // Object with { pinata: "..." }
  showLabels = true, 
  variant = "buttons" 
}) => {
  const [copiedCid, setCopiedCid] = useState("");
  
  // Prepare complete data object for resolution
  const data = {
    pdfUrl,
    jsonUrl,
    finalPdfUrl,
    finalJsonUrl,
    ipfs: ipfs || {},
    ipfsGatewayUrls: ipfsGatewayUrls || {},
    gatewayExamples: gatewayExamples || {},
    pdfCid,
    jsonCid
  };
  
  // Resolve URLs using priority
  const finalJsonUrl_resolved = resolveUrl('json', data);
  const finalPdfUrl_resolved = resolveUrl('pdf', data);
  
  const hasCid = Boolean(finalJsonUrl_resolved || finalPdfUrl_resolved);

  const handleCopy = async (cid) => {
    try {
      await navigator.clipboard.writeText(cid);
      setCopiedCid(cid);
      setTimeout(() => setCopiedCid(""), 2000);
    } catch (error) {
      console.error("Clipboard copy failed", error);
    }
  };

  if (!hasCid) {
    return <div className="cert-empty">Report generating...</div>;
  }

  if (variant === "inline") {
    return (
      <div className="cert-ipfs">
        <span className="cert-ipfs-status">Published</span>
        <div className="cert-ipfs-inline">
          {finalPdfUrl_resolved && (
            <div>
              <a
                className="cert-ipfs-link"
                href={finalPdfUrl_resolved}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open PDF report on IPFS"
                title={pdfCid || finalPdfUrl_resolved}
              >
                <Icon name="file-pdf" size={18} color="#EF4444" />
                {showLabels && "PDF Report"}
                <Icon name="external-link" size={14} color="#6b7280" />
              </a>
              <button className="cert-ipfs-copy" onClick={() => handleCopy(pdfCid)}>
                <Icon name="copy" size={14} />
                {copiedCid === pdfCid ? "Copied" : "Copy"}
              </button>
            </div>
          )}
          {finalJsonUrl_resolved && (
            <div>
              <a
                className="cert-ipfs-link"
                href={finalJsonUrl_resolved}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open JSON on IPFS"
                title={jsonCid || finalJsonUrl_resolved}
              >
                <Icon name="code" size={18} color="#3B82F6" />
                {showLabels && "JSON Data"}
                <Icon name="external-link" size={14} color="#6b7280" />
              </a>
              <button className="cert-ipfs-copy" onClick={() => handleCopy(jsonCid)}>
                <Icon name="copy" size={14} />
                {copiedCid === jsonCid ? "Copied" : "Copy"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cert-ipfs">
      <span className="cert-ipfs-status">Published</span>
      <div className="cert-ipfs-buttons">
        {finalPdfUrl_resolved && (
          <a
            className="cert-ipfs-button"
            href={finalPdfUrl_resolved}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View PDF report"
            style={{ background: "#EF4444" }}
          >
            <Icon name="file-pdf" size={18} />
            {showLabels && "View PDF Report"}
          </a>
        )}
        {finalJsonUrl_resolved && (
          <a
            className="cert-ipfs-button"
            href={finalJsonUrl_resolved}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open JSON data"
            style={{ background: "#3B82F6" }}
          >
            <Icon name="code" size={18} />
            {showLabels && "JSON Data"}
          </a>
        )}
      </div>
    </div>
  );
};

export default IPFSLinks;
